import React, { useState } from 'react';
import { Typography, Button, Form, Card, Row, Col, Input, notification } from 'antd';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { addNoviMailgunKeys } from '@/api/novi/user'; // API function
import { useDispatch } from 'react-redux';
import { updateUserAccount } from '@/store/account/slice';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getLoggedInUser } from '@/store/account/selector';

const { Title, Text } = Typography;

const NoviEmail: React.FC = () => {
	const dispatch = useDispatch();
	const user = useAppSelector(getLoggedInUser);

	const [form] = Form.useForm();
	const [showApiKey, setShowApiKey] = useState(false);
	const [isConfigured, setIsConfigured] = useState(!!user?.mailgunCreds?.apiKey);

	const [noviMailgunDomain, setNoviMailgunDomain] = useState(user.mailgunCreds?.domain || '');
	const [noviMailgunAddress, setNoviMailgunAddress] = useState(user.mailgunCreds?.address || '');
	const noviMailgunUsername = 'api';
	const [noviMailgunApiKey, setNoviMailgunApiKey] = useState(user.mailgunCreds?.apiKey || '');

	const { mutate: saveNoviMailgunConfig, isPending } = useMutation<
		{ message: string },
		Error,
		{
			noviMailgunDomain: string;
			noviMailgunAddress: string;
			noviMailgunUsername: string;
			noviMailgunApiKey: string;
		}
	>({
		mutationFn: addNoviMailgunKeys,
		onSuccess: () => {
			updateUserLocal({
				noviMailgunDomain,
				noviMailgunAddress,
				noviMailgunUsername,
				noviMailgunApiKey,
			});
			setIsConfigured(true);
			notification.success({
				message: 'Success',
				description: 'Mailgun details validated and saved successfully',
				placement: 'bottomRight',
				className: 'compact-notification',
			});
		},
		onError: (error: Error) => {
			notification.error({
				message: 'Error',
				description: error.message,
				placement: 'bottomRight',
				className: 'compact-notification',
			});
		},
	});

	const updateUserLocal = (data: any) => {
		dispatch(
			updateUserAccount({
				userAccount: {
					mailgunCreds: {
						domain: data.noviMailgunDomain,
						address: data.noviMailgunAddress,
						username: data.noviMailgunUsername,
						apiKey: data.noviMailgunApiKey,
					},
				},
			}),
		);
	};

	const handleSave = () => {
		saveNoviMailgunConfig({
			noviMailgunDomain,
			noviMailgunAddress,
			noviMailgunUsername,
			noviMailgunApiKey,
		});
	};

	const handleReconfigure = () => {
		setNoviMailgunDomain('');
		setNoviMailgunAddress('');
		setNoviMailgunApiKey('');
		updateUserLocal({
			noviMailgunDomain: '',
			noviMailgunAddress: '',
			noviMailgunUsername: '',
			noviMailgunApiKey: '',
		});
		setIsConfigured(false);
	};

	return (
		<div className="p-6 w-full bg-gray-50">
			<Row className="justify-center">
				<Col span={18}>
					<Card className="border-0 shadow-lg p-5 rounded-3xl">
						<Title level={3}>Configure Email Notifications</Title>
						<div className="p-4 bg-gray-50 rounded-lg my-8">
							<Text>
								Currently using default email: <strong>notifications@novi.com</strong>
							</Text>
							<br />
							<Text>
								All notifications will be sent from this address unless you configure your own email
								below.
							</Text>
						</div>

						<Title level={4}>Use Your Own Email (Optional)</Title>
						<Text>Connect Mailgun to send emails from your domain.</Text>

						{isConfigured ? (
							<div className="p-4 bg-gray-100 rounded-lg my-4">
								<div className="mb-4">
									<Text strong>Email Domain:</Text> <Text>{noviMailgunDomain}</Text>
								</div>
								<div className="mb-4">
									<Text strong>From Address:</Text> <Text>{noviMailgunAddress}</Text>
								</div>
								<div className="mb-4">
									<Text strong>Mailgun API Key:</Text>{' '}
									<Text>{showApiKey ? noviMailgunApiKey : '********'}</Text>{' '}
									<Button
										type="link"
										icon={showApiKey ? <EyeInvisibleOutlined /> : <EyeOutlined />}
										onClick={() => setShowApiKey(!showApiKey)}
									/>
								</div>
								<Button type="default" onClick={handleReconfigure} className="mt-4">
									Reconfigure
								</Button>
							</div>
						) : (
							<Form form={form} layout="vertical" className="mt-4">
								<Form.Item
									label="Email Domain"
									name="noviMailgunDomain"
									rules={[{ required: true, message: 'Required' }]}
								>
									<Input
										value={noviMailgunDomain}
										onChange={(e) => setNoviMailgunDomain(e.target.value)}
										placeholder="Enter Email Domain"
									/>
								</Form.Item>
								<Form.Item
									label="From Address"
									name="noviMailgunAddress"
									rules={[{ required: true, message: 'Required' }]}
								>
									<Input
										value={noviMailgunAddress}
										onChange={(e) => setNoviMailgunAddress(e.target.value)}
										placeholder="Enter From Address"
									/>
								</Form.Item>
								<Form.Item
									label="Mailgun API Key"
									name="noviMailgunApiKey"
									rules={[{ required: true, message: 'Required' }]}
								>
									<Input.Password
										value={noviMailgunApiKey}
										onChange={(e) => setNoviMailgunApiKey(e.target.value)}
										placeholder="Enter Mailgun API Key"
										iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
										visibilityToggle={{ visible: showApiKey, onVisibleChange: setShowApiKey }}
									/>
								</Form.Item>
								<Button type="primary" loading={isPending} onClick={handleSave} className="mt-4">
									Save Changes
								</Button>
							</Form>
						)}
					</Card>

					<div className="p-4 bg-gray-50 rounded-lg my-4">
						<Text>
							Need help setting up Mailgun? Check our{' '}
							<a
								href="https://www.mailgun.com/resources/videos/getting-started-with-mailgun/"
								target="_blank"
								className="text-blue-500"
							>
								setup guide
							</a>
						</Text>
					</div>
				</Col>
			</Row>
		</div>
	);
};

export default NoviEmail;
